import { UseQueryOptions } from '@tanstack/react-query';
import {
	ItemPage,
	ScopeType,
	VariableConfig,
	deletePath,
	getByIdPath,
	getPagePath,
	savePath,
	scopedClientKey,
	scopedProjectKey,
	secretsControllerPath,
	secretsGetScopedSecretsPath,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	buildURL,
	getTenantID,
	handleResponse,
	standardDelete,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import Parameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import QueryClientWrapper from '../utils/create-query-client';
import HttpResponseOptions from '../interface/http-response-options';

export class SecretsHttpService extends CRUDHelperBase<VariableConfig> {
	constructor() {
		super(secretsControllerPath);
	}

	getScopedSecretsQuery(
		queryParams?: Parameter[]
	): UseQueryOptions<VariableConfig[], Error, VariableConfig[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				secretsGetScopedSecretsPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<VariableConfig[]> => {
				const url = buildURL(this.objectType, [secretsGetScopedSecretsPath], queryParams);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get all', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async saveScoped(
		item: VariableConfig,
		scopeType: ScopeType,
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		invalidateKeys?: boolean
	): Promise<void> {
		const { prefix, headers } = this.getScopedLimits(scopeType, item.scopeID);
		const url = buildURL(this.objectType, [prefix, savePath], queryParams);
		const response = await standardPost(url, item, headers);
		await handleResponse(response, 'save', this.objectType, options);
		if (invalidateKeys) {
			await this.invalidateKeys();
		}
	}

	getScopedPage(
		limit: number,
		offset: number,
		scopeId: string,
		scopeType: ScopeType,
		queryParams?: Parameter[]
	): UseQueryOptions<ItemPage<VariableConfig>, Error, ItemPage<VariableConfig>> {
		if (offset < 0) {
			offset = 0;
		}

		const { prefix, headers } = this.getScopedLimits(scopeType, scopeId);
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				prefix,
				getPagePath,
				limit.toString(),
				offset.toString(),
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<ItemPage<VariableConfig>> => {
				const url = buildURL(
					this.objectType,
					[prefix, getPagePath, limit.toString(), offset.toString()],
					queryParams
				);
				const res = await standardGet(url, headers);
				await validateOkResponse(res, 'get page', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getScopedByID(
		Id: string,
		scopeId: string,
		scopeType: ScopeType,
		queryParams?: Parameter[]
	): UseQueryOptions<VariableConfig, Error, VariableConfig> {
		const { prefix, headers } = this.getScopedLimits(scopeType, scopeId);
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				prefix,
				getByIdPath,
				Id,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<VariableConfig> => {
				const url = buildURL(this.objectType, [prefix, getByIdPath, Id], queryParams);
				const res = await standardGet(url, headers);
				await validateOkResponse(res, 'get by ID', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async deleteScoped(
		itemId: string,
		scopeId: string,
		scopeType: ScopeType,
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		remove?: boolean
	): Promise<void> {
		const { prefix, headers } = this.getScopedLimits(scopeType, scopeId);
		const url = buildURL(this.objectType, [prefix, deletePath, itemId], queryParams);
		const response = await standardDelete(url, undefined, headers);
		await handleResponse(response, 'delete', this.objectType, options);
		await this.invalidateKeys(undefined, remove);
	}

	async invalidateGetScopedVariables(queryParams?: Parameter[], remove = false) {
		await QueryClientWrapper.InvalidateKey(
			[
				getTenantID(),
				this.objectType,
				secretsGetScopedSecretsPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			remove
		);
	}

	private getScopedLimits(scopeType: ScopeType, scopeId: string) {
		const isClient = scopeType === ScopeType.Client;

		const prefix = isClient ? 'Client' : 'Project';
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: scopeId,
		};

		return { prefix, headers };
	}

	async invalidateGetScopedPage(scopeType: ScopeType, remove = false) {
		const isClient = scopeType === ScopeType.Client;
		const prefix = isClient ? 'Client' : 'Project';
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, prefix, getPagePath],
			remove
		);
	}

	async invalidateGetScopedByID(scopeType: ScopeType, remove = false) {
		const isClient = scopeType === ScopeType.Client;
		const prefix = isClient ? 'Client' : 'Project';
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, prefix, getByIdPath],
			remove
		);
	}
}
